import { Box, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { RelatedJobsPanel } from './panels/RelatedJobsPanel';
import { ServiceAdminTab } from './ServiceAdminTab';
import { ServiceDetailDialogs } from './ServiceDetailDialogs';
import { ServiceDetailHeader, ServiceDetailTabKey } from './ServiceDetailHeader';
import { ServiceDetailsTab } from './ServiceDetailsTab';
import { useJobTabTitle } from './useJobTabTitle';
import { useServiceDetailData } from './useServiceDetailData';
import { useServiceDialogs } from './useServiceDialogs';
import { useServiceTags } from './useServiceTags';
import { ActionPermissions } from '../../../@types';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { isUserHasPermission } from '../../../configs/permissions';
import { useIsClient } from '../../../hooks/useIsClient';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { getServiceLocationName } from '../../../utils/service-location';
import { checkServiceCancelOrFutileAvailability } from '../../../utils/service-status';
import { NotFoundError } from '../../Error/NotFound';

export const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const serviceId = id ? parseInt(id, 10) : 0;
  const [activeTab, setActiveTab] = useState<ServiceDetailTabKey>('details');

  const permissions = useGetUserSystemPermissions().data;
  // Client/customer users see a curated Details view only — Administration
  // and Other tabs are removed, and several Details-tab panels are hidden.
  // Detection fail-closes (treats loading/error as "is client") so a flash
  // of the restricted view is preferred over leaking internal panels.
  const isClient = useIsClient();
  const data = useServiceDetailData(serviceId);
  const { service, serviceQuery, clientLogoPic, closedJobsAtLocation } = data;

  useJobTabTitle('Job', id, getServiceLocationName(service));

  const statusCode = service?.serviceStatus?.code ?? '';
  const serviceTypeCode = service?.serviceTypeCode ?? '';
  const dialogs = useServiceDialogs(
    serviceId,
    statusCode,
    serviceTypeCode,
    service?.mainLocationNodeId ?? 0,
    data.handleRefresh
  );
  const { allTags, onSaveTags } = useServiceTags(serviceId, permissions, data.handleRefresh);
  const hasAdminPermission = isUserHasPermission(
    ActionPermissions.Service_Edit_Administration_Section,
    permissions
  );
  // Defense in depth: even if a client/customer somehow has the admin
  // permission, hide the Administration and Other tabs entirely.
  const showAdministrationTab = hasAdminPermission && !isClient;
  const showOtherTab = !isClient;
  const canChangeStatus = isUserHasPermission(
    ActionPermissions.Service_Edit_Status_Progress_Status_Change,
    permissions
  );
  const canAllocate = isUserHasPermission(
    ActionPermissions.Service_Edit_Allocation_Primary,
    permissions
  );
  const canPartAction = isUserHasPermission(
    ActionPermissions.Service_Edit_Part_Actions,
    permissions
  );
  const canCreateTasks = isUserHasPermission(
    ActionPermissions.Service_Edit_Actions_Tasks,
    permissions
  );
  const canReopen = isUserHasPermission(
    ActionPermissions.Service_Edit_Status_Progress_Reopen_Service,
    permissions
  );
  const isClosed = Boolean(service?.serviceStatus?.isClosed);

  if (serviceQuery.isLoading) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (serviceQuery.isError || (!serviceQuery.isLoading && !service)) {
    return (
      <PageContainer>
        <NotFoundError title={id ? `Job ${id} not found` : 'Job not found'} message="" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <ServiceDetailHeader
          serviceId={id ?? ''}
          service={service}
          clientLogoPic={clientLogoPic}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          showAdministrationTab={showAdministrationTab}
          showOtherTab={showOtherTab}
          hideAllocation={isClient}
          allTags={allTags}
          onSaveTags={onSaveTags}
          onStatusChange={canChangeStatus ? dialogs.handleOpenStatusDialog : undefined}
          onAllocate={canAllocate ? dialogs.handleOpenAllocationDialog : undefined}
          onPartAction={canPartAction ? dialogs.handleOpenPartActionDialog : undefined}
          onTask={canCreateTasks ? dialogs.handleOpenTaskDialog : undefined}
          onCancelFutile={
            checkServiceCancelOrFutileAvailability(permissions || [], false, statusCode) &&
            !isClosed
              ? dialogs.handleOpenCancelFutileDialog
              : undefined
          }
          onReopen={canReopen && isClosed ? dialogs.handleOpenReopenDialog : undefined}
          statusDisabledReason={
            isClosed
              ? canReopen
                ? 'This job is closed. Reopen it to change status.'
                : 'This job is closed.'
              : undefined
          }
        />

        <ServiceDetailDialogs
          service={service}
          serviceId={serviceId}
          dialogs={dialogs}
          permissions={permissions}
        />

        {activeTab === 'details' && service && (
          <ServiceDetailsTab
            service={service}
            serviceId={serviceId}
            isClient={isClient}
            permissions={permissions}
            data={data}
          />
        )}

        {activeTab === 'administration' && showAdministrationTab && (
          <ServiceAdminTab serviceId={serviceId} data={data} />
        )}

        {activeTab === 'learn' && showOtherTab && (
          <Box sx={{ px: 2.5, pt: 1.75 }}>
            <RelatedJobsPanel
              title="Closed Jobs at This Location"
              currentServiceId={serviceId}
              otherJobsAtLocation={closedJobsAtLocation}
            />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};
